import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  ExternalLink,
  Calendar,
  Clock,
  User,
  Globe,
  FileText,
  Tag,
  Star,
  Archive,
  MessageSquare,
  Image,
} from 'lucide-react';
import type { BookmarkWithTags } from '../types/database';

interface BookmarkDetailDialogProps {
  bookmark: BookmarkWithTags | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BookmarkDetailDialog({ bookmark, open, onOpenChange }: BookmarkDetailDialogProps) {
  if (!bookmark) return null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col p-0">
        <DialogHeader className="px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            {bookmark.favicon_url && (
              <img
                src={bookmark.favicon_url}
                alt=""
                className="mt-0.5 h-6 w-6 rounded"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <div className="min-w-0 flex-1">
              <DialogTitle className="pr-8 text-lg font-semibold leading-tight">
                {bookmark.title}
              </DialogTitle>
              <a
                href={bookmark.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary"
              >
                <span className="max-w-[400px] truncate">{bookmark.url}</span>
                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
              </a>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1 px-6">
          <div className="space-y-5 pb-6">
            {/* Status badges */}
            <div className="flex flex-wrap gap-2">
              {bookmark.is_favorite && (
                <Badge variant="secondary" className="gap-1">
                  <Star className="h-3 w-3 fill-primary text-primary" />
                  즐겨찾기
                </Badge>
              )}
              {bookmark.is_archived && (
                <Badge variant="secondary" className="gap-1">
                  <Archive className="h-3 w-3" />
                  보관됨
                </Badge>
              )}
            </div>

            {/* Thumbnail */}
            {bookmark.thumbnail_url && (
              <div className="space-y-2">
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Image className="h-4 w-4" />
                  미리보기 이미지
                </h4>
                <img
                  src={bookmark.thumbnail_url}
                  alt="Thumbnail"
                  className="max-h-48 rounded-lg border object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Description */}
            {bookmark.description && (
              <div className="space-y-2">
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  설명
                </h4>
                <p className="text-sm leading-relaxed text-foreground">{bookmark.description}</p>
              </div>
            )}

            {/* Excerpt */}
            {bookmark.excerpt && bookmark.excerpt !== bookmark.description && (
              <div className="space-y-2">
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  요약
                </h4>
                <p className="rounded-lg bg-muted/50 p-3 text-sm leading-relaxed text-foreground">
                  {bookmark.excerpt}
                </p>
              </div>
            )}

            {/* Meta information grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Site name */}
              {bookmark.site_name && (
                <div className="space-y-1">
                  <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Globe className="h-3.5 w-3.5" />
                    사이트
                  </h4>
                  <p className="text-sm">{bookmark.site_name}</p>
                </div>
              )}

              {/* Domain */}
              <div className="space-y-1">
                <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Globe className="h-3.5 w-3.5" />
                  도메인
                </h4>
                <p className="text-sm">{bookmark.domain}</p>
              </div>

              {/* Author */}
              {bookmark.author && (
                <div className="space-y-1">
                  <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <User className="h-3.5 w-3.5" />
                    작성자
                  </h4>
                  <p className="text-sm">{bookmark.author}</p>
                </div>
              )}

              {/* Published date */}
              {bookmark.published_at && (
                <div className="space-y-1">
                  <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    발행일
                  </h4>
                  <p className="text-sm">{formatDate(bookmark.published_at)}</p>
                </div>
              )}

              {/* Reading time */}
              {bookmark.reading_time && bookmark.reading_time > 0 && (
                <div className="space-y-1">
                  <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    읽는 시간
                  </h4>
                  <p className="text-sm">약 {bookmark.reading_time}분</p>
                </div>
              )}

              {/* Created date */}
              <div className="space-y-1">
                <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  저장일
                </h4>
                <p className="text-sm">{formatDate(bookmark.created_at)}</p>
              </div>
            </div>

            {/* Tags */}
            {bookmark.tags.length > 0 && (
              <div className="space-y-2">
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Tag className="h-4 w-4" />
                  태그
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {bookmark.tags.map((tag) => (
                    <Badge key={tag.id} variant="secondary">
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {bookmark.notes && (
              <div className="space-y-2">
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <MessageSquare className="h-4 w-4" />
                  메모
                </h4>
                <div className="whitespace-pre-wrap rounded-lg bg-accent/50 p-3 text-sm text-accent-foreground">
                  {bookmark.notes}
                </div>
              </div>
            )}

            {/* Content preview */}
            {bookmark.content && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <FileText className="h-4 w-4" />
                    본문 내용
                    <span className="text-xs font-normal">
                      ({bookmark.content.length.toLocaleString()}자)
                    </span>
                  </h4>
                  <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-muted/30 p-4 text-sm leading-relaxed text-muted-foreground">
                    {bookmark.content}
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        <div className="border-t px-6 py-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              마지막 업데이트: {formatDate(bookmark.updated_at)}
            </span>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              닫기
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
